// src/pages/accounting/HorseFinanceCenter.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Tabs,
  Tab,
  Stack,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  fetchSummary,
  fetchHorseLedger, // for Inside
  fetchClientStatements,
  fetchAccommodationKPIs,
  fetchTransportKPIs,
} from '../../api/accountingReports';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

/* ----------------------------- Utilities ----------------------------- */

// Returns { from: YYYY-MM-01, to: YYYY-MM-DD (today) }
function getDefaultMonthRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return { from: `${y}-${m}-01`, to: `${y}-${m}-${d}` };
}

// Build "Arabic / English | Owner" safely
function formatHorseOption(opt, t) {
  if (!opt || typeof opt === 'string') return opt || '';
  const ar = (opt.labelAr || opt.name_ar || '').trim();
  const en = (opt.labelEn || opt.name || '').trim();
  const owner = (opt.owner_name || '').trim();
  const name = [ar, en].filter(Boolean).join(' / ');
  const base = name || t?.('unknown_horse') || 'unknown_horse';
  return owner ? `${base} | ${owner}` : base;
}

// Generic fetch that normalizes common shapes {ok, data} or [] directly
async function fetchJSON(url) {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const data = await r.json();
  return Array.isArray(data) ? data : data.data ?? data;
}

// Stable unique key for options (origin + id + fallback)
function horseKey(opt, prefix = '') {
  const origin = (opt?.origin || 'Inside').toLowerCase();
  const id = opt?.id ?? opt?.horse_id ?? opt?.value ?? '';
  return `${prefix}${origin}:${id || 'noid'}`;
}

// Normalize API rows -> {id, labelAr, labelEn, owner_name, origin}
function normalizeHorses(rows = [], fallbackOrigin = 'Inside') {
  return rows.map((h) => ({
    id: h.id ?? h.horse_id ?? h.value ?? h.key,
    origin: h.origin ?? h.horse_origin ?? fallbackOrigin,
    labelAr:
      h.labelAr ??
      h.name_ar ??
      h.horse_name_ar ??
      h.horse_name_arabic ??
      h.name_arabic ??
      '',
    labelEn:
      h.labelEn ??
      h.name_en ??
      h.horse_name ??
      h.horse_name_en ??
      h.name ??
      '',
    owner_name:
      h.owner_name ??
      h.client_name ??
      h.owner ??
      h.owner_en ??
      h.owner_ar ??
      '',
  }));
}

/** Lookup inside: horse or owner */
async function searchHorsesInside(term) {
  const q = encodeURIComponent(term || '');
  try {
    const rows = await fetchJSON(
      `${apiUrl}/accounting-reports/horses/lookup-inside?q=${q}&limit=30`
    );
    return normalizeHorses(rows, 'Inside');
  } catch (_) {
    return [];
  }
}

/** Lookup outside: horse or client */
async function searchHorsesOutside(term) {
  const q = encodeURIComponent(term || '');
  try {
    const rows = await fetchJSON(
      `${apiUrl}/accounting-reports/horses/lookup-outside?q=${q}&limit=30`
    );
    return normalizeHorses(rows, 'Outside');
  } catch (_) {
    return [];
  }
}

/** Search clients (outside) */
async function searchClients(term) {
  const q = encodeURIComponent(term || '');
  const urls = [
    `${apiUrl}/vetclinic/clients/lookup?q=${q}`,
    `${apiUrl}/clients/lookup?q=${q}`,
    `${apiUrl}/vetclinic/clients?search=${q}`,
  ];
  for (const u of urls) {
    try {
      const rows = await fetchJSON(u);
      return rows.map((c) => ({
        id: c.client_id ?? c.id ?? c.value ?? c.key,
        labelAr: c.client_name ?? c.name_ar ?? c.name,
        labelEn: c.client_name_en ?? c.name_en ?? c.client_name ?? c.name,
      }));
    } catch {}
  }
  return [];
}

// Money formatter (thousands separator)
const moneyFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const fmtMoney = (n) => moneyFormatter.format(Number(n || 0));

// normalize any value into a safe Array (for .map())
// comments: English only
const toArray = (v) => {
  if (Array.isArray(v)) return v;
  if (v && Array.isArray(v.rows)) return v.rows; // handle {rows:[...]}
  if (v && typeof v === 'object') return Object.values(v); // keyed object => values
  return [];
};

/** Search owners (inside) */
async function searchOwners(term) {
  const q = encodeURIComponent(term || '');
  const urls = [
    `${apiUrl}/owners/lookup?q=${q}`,
    `${apiUrl}/owners?search=${q}`,
  ];
  for (const u of urls) {
    try {
      const rows = await fetchJSON(u);
      // normalize to {id, labelAr, labelEn}
      return rows.map((r) => ({
        id: r.id ?? r.owner_id ?? r.value ?? r.key,
        labelAr: r.name_ar ?? r.owner_name_ar ?? r.name ?? r.owner_name,
        labelEn: r.name_en ?? r.owner_name_en ?? r.name ?? r.owner_name,
      }));
    } catch {}
  }
  return [];
}

// Build API URL from env (same pattern used across project)
const apiUrl = process.env.REACT_APP_STORYBOOK_URL;

/* -------------------------- Small Reusable Bits -------------------------- */

function DateRange({ value, onChange, t }) {
  // comments: English only
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextField
        type="date"
        label={t('from') || 'من'}
        value={value.from}
        onChange={(e) => onChange({ ...value, from: e.target.value })}
        size="small"
      />
      <TextField
        type="date"
        label={t('to') || 'إلى'}
        value={value.to}
        onChange={(e) => onChange({ ...value, to: e.target.value })}
        size="small"
      />
    </Stack>
  );
}

function KPI({ title, value }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid #eee',
        borderRadius: 3,
        textAlign: 'center',
        display: 'grid',
        gap: 0.5,
        justifyItems: 'center',
      }}
   >
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ fontWeight: 600 }}
      >
        {title}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Paper>
  );
}

/* ---------------------------- Main Component ---------------------------- */

export default function HorseFinanceCenter() {
  const { t } = useTranslation();

  // tabs: 0=summary, 1=ledger inside, 2=ledger outside, 3=client statements,
  // 4=owner statements, 5=accommodation, 6=transport
  const [tab, setTab] = useState(0);
  const [range, setRange] = useState(getDefaultMonthRange());

  // summary
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // inside ledger
  const [horseInInput, setHorseInInput] = useState('');
  const [horseInSel, setHorseInSel] = useState(null);
  const [horseInOpen, setHorseInOpen] = useState(false);
  const [horseInOptions, setHorseInOptions] = useState([]);
  const [horseInLoading, setHorseInLoading] = useState(false);
  const [ledgerInside, setLedgerInside] = useState([]);
  const [loadingLedgerIn, setLoadingLedgerIn] = useState(false);

  // outside ledger
  const [horseOutInput, setHorseOutInput] = useState('');
  const [horseOutSel, setHorseOutSel] = useState(null);
  const [horseOutOpen, setHorseOutOpen] = useState(false);
  const [horseOutOptions, setHorseOutOptions] = useState([]);
  const [horseOutLoading, setHorseOutLoading] = useState(false);
  const [ledgerOutside, setLedgerOutside] = useState([]);
  const [loadingLedgerOut, setLoadingLedgerOut] = useState(false);

  // clients (outside)
  const [clientInput, setClientInput] = useState('');
  const [clientSel, setClientSel] = useState(null);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientStatements, setClientStatements] = useState([]);
  const [clientStmtLoading, setClientStmtLoading] = useState(false);

  // owners (inside)
  const [ownerInput, setOwnerInput] = useState('');
  const [ownerSel, setOwnerSel] = useState(null);
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [ownerStatements, setOwnerStatements] = useState([]);
  const [ownerStmtLoading, setOwnerStmtLoading] = useState(false);

  // accommodation KPIs
  const [acc, setAcc] = useState(null);
  const [accLoading, setAccLoading] = useState(false);

  // transport KPIs
  const [trn, setTrn] = useState(null);
  const [trnLoading, setTrnLoading] = useState(false);

  const horseInId = horseInSel?.id ?? horseInSel?.horse_id;
  const horseOutId = horseOutSel?.id ?? horseOutSel?.horse_id;
  const clientId = clientSel?.id;
  const ownerId = ownerSel?.id;

  /* ------------------------------ Effects: lookup ------------------------------ */

  useEffect(() => {
    let active = true;
    if (!horseInOpen) return;
    setHorseInLoading(true);
    const tid = setTimeout(async () => {
      const rows = await searchHorsesInside(horseInInput || '');
      if (!active) return;
      setHorseInOptions(rows);
      setHorseInLoading(false);
    }, 300);
    return () => {
      active = false;
      clearTimeout(tid);
    };
  }, [horseInInput, horseInOpen]);

  useEffect(() => {
    let active = true;
    if (!horseOutOpen) return;
    setHorseOutLoading(true);
    const tid = setTimeout(async () => {
      const rows = await searchHorsesOutside(horseOutInput || '');
      if (!active) return;
      setHorseOutOptions(rows);
      setHorseOutLoading(false);
    }, 300);
    return () => {
      active = false;
      clearTimeout(tid);
    };
  }, [horseOutInput, horseOutOpen]);

  useEffect(() => {
    let active = true;
    if (!clientInput) {
      setClientOptions([]);
      return;
    }
    setClientLoading(true);
    const tid = setTimeout(async () => {
      const rows = await searchClients(clientInput || '');
      if (!active) return;
      setClientOptions(rows);
      setClientLoading(false);
    }, 300);
    return () => {
      active = false;
      clearTimeout(tid);
    };
  }, [clientInput]);

  useEffect(() => {
    let active = true;
    if (!ownerInput) {
      setOwnerOptions([]);
      return;
    }
    setOwnerLoading(true);
    const tid = setTimeout(async () => {
      const rows = await searchOwners(ownerInput || '');
      if (!active) return;
      setOwnerOptions(rows);
      setOwnerLoading(false);
    }, 300);
    return () => {
      active = false;
      clearTimeout(tid);
    };
  }, [ownerInput]);

  /* ------------------------------- Effects: data ------------------------------- */

  useEffect(() => {
    if (tab === 0) {
      setLoadingSummary(true);
      fetchSummary({ ...range, bucket: 'monthly' })
        .then((r) => setSummary(r?.data ?? r))
        .finally(() => setLoadingSummary(false));
      return;
    }

    if (tab === 1 && horseInId) {
      setLoadingLedgerIn(true);
      fetchHorseLedger({ ...range, horse_id: horseInId })
        .then((r) => setLedgerInside(r?.data ?? r))
        .finally(() => setLoadingLedgerIn(false));
      return;
    }

    if (tab === 2 && horseOutId) {
      setLoadingLedgerOut(true);
      // outside ledger API can be a dedicated endpoint; using fetchJSON example
      const url = `${apiUrl}/accounting-reports/horse-ledger-outside?horse_id=${encodeURIComponent(
        horseOutId
      )}&from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(
        range.to
      )}`;
      fetchJSON(url)
        .then((rows) => setLedgerOutside(rows))
        .finally(() => setLoadingLedgerOut(false));
      return;
    }

    if (tab === 3) {
      // Client Statements (outside)
      setClientStmtLoading(true);
      fetchClientStatements({ ...range, client_id: clientId || undefined })
        .then((r) => setClientStatements(r?.data ?? r))
        .finally(() => setClientStmtLoading(false));
      return;
    }

    if (tab === 4) {
      // Owner Statements (inside)
      setOwnerStmtLoading(true);
      const url = `${apiUrl}/accounting-reports/owners/statement?owner_id=${
        ownerId || ''
      }&from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(
        range.to
      )}`;
      fetchJSON(url)
        .then((rows) => setOwnerStatements(rows))
        .finally(() => setOwnerStmtLoading(false));
      return;
    }

    if (tab === 5) {
      setAccLoading(true);
      fetchAccommodationKPIs(range)
        .then((r) => setAcc(r?.data ?? r))
        .finally(() => setAccLoading(false));
      return;
    }

    if (tab === 6) {
      setTrnLoading(true);
      fetchTransportKPIs(range)
        .then((r) => setTrn(r?.data ?? r))
        .finally(() => setTrnLoading(false));
      return;
    }
  }, [tab, range, horseInId, horseOutId, clientId, ownerId]);

  // Summary totals: be robust to shapes (array, {rows:[]}, keyed object)
  const totals = useMemo(() => {
    const rawTotals = summary?.totals ?? summary?.data?.totals ?? null;
    let rows = [];
    if (Array.isArray(rawTotals)) {
      rows = rawTotals;
    } else if (rawTotals && typeof rawTotals === 'object') {
      rows = Array.isArray(rawTotals.rows) ? rawTotals.rows : Object.values(rawTotals);
    }
    const get = (k) => {
      const rec = rows.find((x) => (x?.service ?? x?.SERVICE) === k);
      const val = rec?.revenue ?? rec?.REVENUE ?? 0;
      return Number(val) || 0;
    };
    return {
      vet: get('vetclinic'),
      far: get('farrier'),
      trn: get('transportation'),
      acc: get('accommodation'),
    };
  }, [summary]);

  // Export helper
  const exportTable = (rows, filename) => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'data');
    const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    const blob = new Blob([out], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, filename);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" align="center" sx={{ mb: 3, fontWeight: 700 }}>
        {t('horse_finance_center') || 'مركز تقارير الخيل – محاسبة'}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <DateRange value={range} onChange={setRange} t={t} />
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        <Tab label={t('summary') || 'الملخص'} />
        <Tab label={t('horse_ledger_inside') || 'دفتر الخيل (داخل)'} />
        <Tab label={t('horse_ledger_outside') || 'دفتر الخيل (خارج)'} />
        <Tab label={t('client_statements') || 'كشوف العملاء'} />
        <Tab label={t('owner_statements') || 'كشوف الملاك'} />
        <Tab label={t('accommodation') || 'السكن'} />
        <Tab label={t('transportation') || 'النقل'} />
      </Tabs>

      {/* ------------------------------ Summary ------------------------------ */}
      {tab === 0 && (
        <Box sx={{ mt: 3 }}>
          {loadingSummary ? (
            <Typography align="center" sx={{ my: 4 }}>
              {t('loading') || 'جارِ التحميل...'}
            </Typography>
          ) : (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <KPI title={t('vetclinic') || 'العيادة'} value={fmtMoney(totals.vet)} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <KPI title={t('farrier') || 'الحدّاد'} value={fmtMoney(totals.far)} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <KPI title={t('transport') || 'النقل'} value={fmtMoney(totals.trn)} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <KPI title={t('accommodation') || 'السكن'} value={fmtMoney(totals.acc)} />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t('top_spending_horses') || 'أكثر الخيول إنفاقًا'}
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>{t('horse') || 'الخيل'}</TableCell>
                        <TableCell>{t('owner') || 'المالك'}</TableCell>
                        <TableCell>{t('total') || 'الإجمالي'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {toArray(summary?.topHorses).map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell>{r.horse_name || r.labelAr || r.labelEn}</TableCell>
                          <TableCell>{r.owner_name || '-'}</TableCell>
                          <TableCell>{fmtMoney(r.total || r.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* ------------------------- Horse Ledger (INSIDE) ------------------------- */}
      {tab === 1 && (
        <Box sx={{ mt: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <Autocomplete
              sx={{ flex: 1, minWidth: 600 }} // widen field
              open={horseInOpen}
              onOpen={() => setHorseInOpen(true)}
              onClose={() => setHorseInOpen(false)}
              options={horseInOptions}
              loading={horseInLoading}
              value={horseInSel}
              onChange={(_, v) => setHorseInSel(v)}
              onInputChange={(_, v) => setHorseInInput(v)}
              filterOptions={(x) => x}
              getOptionLabel={(opt) => formatHorseOption(opt, t)}
              renderOption={(props, option, { index }) => {
                const { key, ...liProps } = props;
                return (
                  <li
                    {...liProps}
                    key={horseKey(option, 'in:') || `in:${index}`}
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatHorseOption(option, t)}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('select_horse_inside') || 'اختر الخيل (داخل)'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {horseInLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Button
              variant="contained"
              disabled={!horseInId}
              onClick={() => {
                if (!horseInId) return;
                setLoadingLedgerIn(true);
                fetchHorseLedger({ ...range, horse_id: horseInId })
                  .then((r) => setLedgerInside(r?.data ?? r))
                  .finally(() => setLoadingLedgerIn(false));
              }}
            >
              {t('load') || 'تحميل'}
            </Button>
            <Button
              variant="outlined"
              disabled={!toArray(ledgerInside).length}
              onClick={() => exportTable(ledgerInside, 'horse-ledger-inside.xlsx')}
            >
              {t('export') || 'تصدير'}
            </Button>
          </Stack>

          {loadingLedgerIn ? (
            <Typography align="center" sx={{ my: 4 }}>
              {t('loading') || 'جارِ التحميل...'}
            </Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('date') || 'التاريخ'}</TableCell>
                    <TableCell>{t('service_type') || 'الخدمة'}</TableCell>
                    <TableCell>{t('record_id') || 'الرقم'}</TableCell>
                    <TableCell>{t('amount') || 'المبلغ'}</TableCell>
                    <TableCell>{t('invoice_number') || 'رقم الفاتورة'}</TableCell>
                    <TableCell>{t('reviewed') || 'مراجَع'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {toArray(ledgerInside).map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        {new Date(r.op_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{r.service_type}</TableCell>
                      <TableCell>{r.record_id}</TableCell>
                      <TableCell>{fmtMoney(r.amount)}</TableCell>
                      <TableCell>{r.invoice_number || '-'}</TableCell>
                      <TableCell>
                        {r.reviewed_by_accountant ? t('yes') || 'نعم' : t('no') || 'لا'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ------------------------- Horse Ledger (OUTSIDE) ------------------------ */}
      {tab === 2 && (
        <Box sx={{ mt: 3 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Autocomplete
              sx={{ flex: 1, minWidth: 600 }}
              open={horseOutOpen}
              onOpen={() => setHorseOutOpen(true)}
              onClose={() => setHorseOutOpen(false)}
              options={horseOutOptions}
              loading={horseOutLoading}
              value={horseOutSel}
              onChange={(_, v) => setHorseOutSel(v)}
              onInputChange={(_, v) => setHorseOutInput(v)}
              filterOptions={(x) => x}
              getOptionLabel={(opt) => formatHorseOption(opt, t)}
              renderOption={(props, option, { index }) => {
                const { key, ...liProps } = props;
                return (
                  <li
                    {...liProps}
                    key={horseKey(option, 'out:') || `out:${index}`}
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatHorseOption(option, t)}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('select_horse_outside') || 'اختر الخيل (خارج)'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {horseOutLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Button
              variant="contained"
              disabled={!horseOutId}
              onClick={() => {
                if (!horseOutId) return;
                setLoadingLedgerOut(true);
                const url = `${apiUrl}/accounting-reports/horse-ledger-outside?horse_id=${encodeURIComponent(
                  horseOutId
                )}&from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(
                  range.to
                )}`;
                fetchJSON(url)
                  .then((rows) => setLedgerOutside(rows))
                  .finally(() => setLoadingLedgerOut(false));
              }}
            >
              {t('load') || 'تحميل'}
            </Button>
            <Button
              variant="outlined"
              disabled={!toArray(ledgerOutside).length}
              onClick={() => exportTable(ledgerOutside, 'horse-ledger-outside.xlsx')}
            >
              {t('export') || 'تصدير'}
            </Button>
          </Stack>

          {loadingLedgerOut ? (
            <Typography align="center" sx={{ my: 4 }}>
              {t('loading') || 'جارِ التحميل...'}
            </Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('date') || 'التاريخ'}</TableCell>
                    <TableCell>{t('service_type') || 'الخدمة'}</TableCell>
                    <TableCell>{t('record_id') || 'الرقم'}</TableCell>
                    <TableCell>{t('amount') || 'المبلغ'}</TableCell>
                    <TableCell>
                      {t('invoice_number') || 'رقم الفاتورة'}
                    </TableCell>
                    <TableCell>{t('reviewed') || 'مراجَع'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {toArray(ledgerOutside).map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        {new Date(r.op_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{r.service_type}</TableCell>
                      <TableCell>{r.record_id}</TableCell>
                      <TableCell>{fmtMoney(r.amount)}</TableCell>
                      <TableCell>{r.invoice_number || '-'}</TableCell>
                      <TableCell>
                        {r.reviewed_by_accountant
                          ? t('yes') || 'نعم'
                          : t('no') || 'لا'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* --------------------------- Client Statements -------------------------- */}
      {tab === 3 && (
        <Box sx={{ mt: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <Autocomplete
              sx={{ flex: 1, minWidth: 400 }}
              options={clientOptions}
              loading={clientLoading}
              value={clientSel}
              onChange={(_, v) => setClientSel(v)}
              onInputChange={(_, v) => setClientInput(v)}
              filterOptions={(x) => x}
              getOptionLabel={(opt) =>
                (opt?.labelAr || opt?.labelEn || '').trim() ||
                t('unknown_client') ||
                'unknown_client'
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('select_client') || 'اختر العميل'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {clientLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Button
              variant="contained"
              onClick={() => {
                setClientStmtLoading(true);
                fetchClientStatements({ ...range, client_id: clientId || undefined })
                  .then((r) => setClientStatements(r?.data ?? r))
                  .finally(() => setClientStmtLoading(false));
              }}
            >
              {t('load') || 'تحميل'}
            </Button>
            <Button
              variant="outlined"
              disabled={!toArray(clientStatements).length}
              onClick={() => exportTable(clientStatements, 'client-statements.xlsx')}
            >
              {t('export') || 'تصدير'}
            </Button>
          </Stack>

          {clientStmtLoading ? (
            <Typography align="center" sx={{ my: 4 }}>
              {t('loading') || 'جارِ التحميل...'}
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {toArray(clientStatements).map((c) => (
                <Grid key={c.client_id ?? c.id ?? c.client_name} item xs={12}>
                  <Paper sx={{ p: 2, borderRadius: 3, border: '1px solid #eee' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {c.client_name || 'بدون اسم'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('total') || 'الإجمالي'}: {fmtMoney(c.total)}
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('date') || 'التاريخ'}</TableCell>
                            <TableCell>
                              {t('service_type') || 'الخدمة'}
                            </TableCell>
                            <TableCell>{t('horse_name') || 'الخيل'}</TableCell>
                            <TableCell>{t('amount') || 'المبلغ'}</TableCell>
                            <TableCell>
                              {t('invoice_number') || 'رقم الفاتورة'}
                            </TableCell>
                            <TableCell>{t('reviewed') || 'مراجَع'}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(c.items || []).map((r, i) => (
                            <TableRow key={i}>
                              <TableCell>
                                {new Date(r.op_date).toLocaleDateString()}
                              </TableCell>
                              <TableCell>{r.service_type}</TableCell>
                              <TableCell>{r.horse_name || '-'}</TableCell>
                              <TableCell>{fmtMoney(r.amount)}</TableCell>
                              <TableCell>{r.invoice_number || '-'}</TableCell>
                              <TableCell>
                                {r.reviewed_by_accountant
                                  ? t('yes') || 'نعم'
                                  : t('no') || 'لا'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* ---------------------------- Owner Statements --------------------------- */}
      {tab === 4 && (
        <Box sx={{ mt: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <Autocomplete
              sx={{ flex: 1, minWidth: 400 }}
              options={ownerOptions}
              loading={ownerLoading}
              value={ownerSel}
              onChange={(_, v) => setOwnerSel(v)}
              onInputChange={(_, v) => setOwnerInput(v)}
              filterOptions={(x) => x}
              getOptionLabel={(opt) =>
                (opt?.labelAr || opt?.labelEn || '').trim() ||
                t('unknown_owner') ||
                'unknown_owner'
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('select_owner') || 'اختر المالك'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {ownerLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Button
              variant="contained"
              onClick={() => {
                setOwnerStmtLoading(true);
                const url = `${apiUrl}/accounting-reports/owners/statement?owner_id=${
                  ownerId || ''
                }&from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(
                  range.to
                )}`;
                fetchJSON(url)
                  .then((rows) => setOwnerStatements(rows))
                  .finally(() => setOwnerStmtLoading(false));
              }}
            >
              {t('load') || 'تحميل'}
            </Button>
            <Button
              variant="outlined"
              disabled={!toArray(ownerStatements).length}
              onClick={() => exportTable(ownerStatements, 'owner-statements.xlsx')}
            >
              {t('export') || 'تصدير'}
            </Button>
          </Stack>

          {ownerStmtLoading ? (
            <Typography align="center" sx={{ my: 4 }}>
              {t('loading') || 'جارِ التحميل...'}
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {toArray(ownerStatements).map((o) => (
                <Grid key={o.owner_id ?? o.id ?? o.owner_name} item xs={12}>
                  <Paper
                    sx={{ p: 2, borderRadius: 3, border: '1px solid #eee' }}
                  >
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {o.owner_name || 'بدون اسم'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {t('total') || 'الإجمالي'}: {fmtMoney(o.total)}
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('date') || 'التاريخ'}</TableCell>
                            <TableCell>
                              {t('service_type') || 'الخدمة'}
                            </TableCell>
                            <TableCell>{t('horse_name') || 'الخيل'}</TableCell>
                            <TableCell>{t('amount') || 'المبلغ'}</TableCell>
                            <TableCell>
                              {t('invoice_number') || 'رقم الفاتورة'}
                            </TableCell>
                            <TableCell>{t('reviewed') || 'مراجَع'}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(o.items || []).map((r, i) => (
                            <TableRow key={i}>
                              <TableCell>
                                {new Date(r.op_date).toLocaleDateString()}
                              </TableCell>
                              <TableCell>{r.service_type}</TableCell>
                              <TableCell>{r.horse_name || '-'}</TableCell>
                              <TableCell>{fmtMoney(r.amount)}</TableCell>
                              <TableCell>{r.invoice_number || '-'}</TableCell>
                              <TableCell>
                                {r.reviewed_by_accountant
                                  ? t('yes') || 'نعم'
                                  : t('no') || 'لا'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* ----------------------------- Accommodation KPIs ---------------------------- */}
      {tab === 5 && (
        <Box sx={{ mt: 3 }}>
          {accLoading ? (
            <Typography align="center" sx={{ my: 4 }}>
              {t('loading') || 'جارِ التحميل...'}
            </Typography>
          ) : (
            <>
              <Grid container spacing={2}>
                {toArray(acc?.occupancy).map((m, i) => (
                  <Grid key={i} item xs={12} md={3}>
                    <KPI title={m.room_name || m.name || `Room ${i + 1}`} value={m.count ?? m.value ?? 0} />
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ mt: 2 }}>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('month') || 'الشهر'}</TableCell>
                        <TableCell>
                          {t('est_revenue') || 'إيراد تقديري'}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {toArray(acc?.revenueEst).map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{r.month}</TableCell>
                          <TableCell>{fmtMoney(r.est_revenue)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* ----------------------------- Transport KPIs ---------------------------- */}
      {tab === 6 && (
        <Box sx={{ mt: 3 }}>
          {trnLoading ? (
            <Typography align="center" sx={{ my: 4 }}>
              {t('loading') || 'جارِ التحميل...'}
            </Typography>
          ) : (
            <>
              <Grid container spacing={2}>
                {toArray(trn?.byCapacity).map((r, i) => (
                  <Grid key={i} item xs={12} md={3}>
                    <KPI
                      title={
                        r.capacity
                          ? `${t('capacity') || 'السعة'}: ${r.capacity}`
                          : `Bucket ${i + 1}`
                      }
                      value={fmtMoney(r.total)}
                    />
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ mt: 2 }}>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('route') || 'المسار'}</TableCell>
                        <TableCell>
                          {t('total_amount') || 'إجمالي المبلغ'}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {toArray(trn?.topRoutes).map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{r.route || '-'}</TableCell>
                          <TableCell>{fmtMoney(r.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
